import { makeAutoObservable, runInAction } from "mobx";
import { CreateBookingService, IAvailableSlot, IBookingService } from "./CreateBookingService";
import { AnalyticsClient } from "../../AnalyticsClient/AnalyticsClient";
import { LoginService } from "../Login/LoginService";
import { YandexMetrika } from "../../AnalyticsClient/YandexMetrika";

export interface ICalendarDay {
  date: string;
  dayNumber: number;
  isCurrentMonth: boolean;
  isAvailableRange: boolean;
  isToday: boolean;
}

export interface IBookingCalendar {
  title: string;
  days: ICalendarDay[];
}

export interface IAvailableDateCard {
  date: string;
  dayNumber: string;
  weekday: string;
  month: string;
  isToday: boolean;
}

const MIN_BOOKING_DELAY_MINUTES = 30;

export class CreateBookingStore {
  public MasterId: number | null = null;

  public Services: IBookingService[] = [];
  public Slots: IAvailableSlot[] = [];

  public SelectedServiceId: number | null = null;
  public SelectedDate = "";
  public SelectedSlot: IAvailableSlot | null = null;

  public IsCalendarOpen = false;

  public IsLoading = false;
  public IsLoadingSlots = false;
  public Error = "";

  public IsCreating = false;
  public CreatedBookingId: number | null = null;
  public SuccessMessage = "";
  public Source = "direct";

  public Phone = "";
  public PhoneDraft = "";
  public HasSavedPhone = false;
  public PhoneError = "";

  public AvailableDates: string[] = [];
  public IsLoadingDates = false;
  public DatesError = "";

  private availableDatesRequestId = 0;

  public CurrentMonthDate = new Date();
  public Calendar: IBookingCalendar = this.buildCalendar();

  constructor() {
    makeAutoObservable(this);
  }

  public get CanGoPrevMonth() {
    const today = new Date();

    return !(this.CurrentMonthDate.getMonth() === today.getMonth() && this.CurrentMonthDate.getFullYear() === today.getFullYear());
  }

  public get CanGoNextMonth() {
    const today = new Date();
    const limit = new Date(today);

    limit.setMonth(limit.getMonth() + 1);

    return this.CurrentMonthDate.getFullYear() < limit.getFullYear() || (this.CurrentMonthDate.getFullYear() === limit.getFullYear() && this.CurrentMonthDate.getMonth() < limit.getMonth());
  }

  public setSource(value: string | null) {
    this.Source = value?.trim() || "direct";
  }

  public goPrevMonth() {
    if (!this.CanGoPrevMonth) return;

    const next = new Date(this.CurrentMonthDate);
    next.setMonth(next.getMonth() - 1);

    this.CurrentMonthDate = next;
    this.Calendar = this.buildCalendar();
  }

  public goNextMonth() {
    if (!this.CanGoNextMonth) return;

    const next = new Date(this.CurrentMonthDate);
    next.setMonth(next.getMonth() + 1);

    this.CurrentMonthDate = next;
    this.Calendar = this.buildCalendar();
  }

  public async createBooking(onSuccess?: () => void) {
    if (!this.MasterId || !this.SelectedServiceId || !this.SelectedSlot) {
      this.Error = "Выберите услугу, день и время";
      return;
    }

    if (!this.isSlotAvailableByTime(this.SelectedSlot)) {
      this.Error = "Это время уже недоступно. Выберите другое время.";
      this.SelectedSlot = null;
      this.Slots = this.filterAvailableSlotsByTime(this.Slots);
      return;
    }

    if (!this.validatePhone()) {
      return;
    }

    this.IsCreating = true;
    this.Error = "";

    try {
      const response = await CreateBookingService.CreateBooking({
        masterId: this.MasterId,
        serviceId: this.SelectedServiceId,
        startsAt: this.SelectedSlot.startsAt,
        phone: this.PhoneDraft.trim(),
      });

      YandexMetrika.reachGoal("booking_created", {
        bookingId: response.id,
        masterId: this.MasterId,
        serviceId: this.SelectedServiceId,
        startsAt: this.SelectedSlot.startsAt,
        phoneProvided: !!this.PhoneDraft.trim(),
      });

      runInAction(() => {
        this.CreatedBookingId = response.id;
        this.SuccessMessage = "Вы записаны";
        AnalyticsClient.track({
          eventType: "booking_created",
          source: this.Source,
          entityType: "booking",
          entityId: response.id,
          metadata: {
            masterId: this.MasterId,
            serviceId: this.SelectedServiceId,
            startsAt: this.SelectedSlot?.startsAt,
            phoneProvided: !!this.PhoneDraft.trim(),
          },
        });
      });

      onSuccess?.();
    } catch {
      runInAction(() => {
        this.Error = "Не удалось создать запись. Возможно, это время уже занято.";
      });
    } finally {
      runInAction(() => {
        this.IsCreating = false;
      });
    }
  }

  public async load(masterId: number) {
    this.MasterId = masterId;
    this.IsLoading = true;
    this.Error = "";
    this.PhoneError = "";

    try {
      const [services, user] = await Promise.all([CreateBookingService.GetMasterServices(masterId), LoginService.GetUser()]);

      runInAction(() => {
        this.Services = services.filter((x) => x.isActive);

        this.Phone = user.phone ?? "";
        this.PhoneDraft = user.phone ?? "";
        this.HasSavedPhone = !!user.phone?.trim();
      });
    } catch {
      runInAction(() => {
        this.Error = "Не удалось загрузить данные для записи";
      });
    } finally {
      runInAction(() => {
        this.IsLoading = false;
      });
    }
  }

  public async selectService(serviceId: number) {
    if (this.SelectedServiceId === serviceId) return;

    this.SelectedServiceId = serviceId;
    this.SelectedDate = "";
    this.SelectedSlot = null;
    this.Slots = [];
    this.AvailableDates = [];
    this.DatesError = "";
    this.CurrentMonthDate = new Date();
    this.Calendar = this.buildCalendar();
    this.IsCalendarOpen = false;

    AnalyticsClient.track({
      eventType: "booking_service_selected",
      source: this.Source,
      entityType: "service",
      entityId: serviceId,
      metadata: {
        masterId: this.MasterId,
      },
    });

    await this.loadAvailableDates();
  }

  public toggleCalendar() {
    this.IsCalendarOpen = !this.IsCalendarOpen;
  }

  public get AvailableDateCards(): IAvailableDateCard[] {
    return this.AvailableDates.map((date) => {
      const parsed = this.parseDateKey(date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      return {
        date,
        dayNumber: String(parsed.getDate()),
        weekday: new Intl.DateTimeFormat("ru-RU", { weekday: "short" }).format(parsed).replace(".", ""),
        month: new Intl.DateTimeFormat("ru-RU", { month: "short" }).format(parsed).replace(".", ""),
        isToday: parsed.getTime() === today.getTime(),
      };
    });
  }

  private parseDateKey(date: string) {
    const [yyyy, mm, dd] = date.split("-").map(Number);

    return new Date(yyyy, mm - 1, dd);
  }

  public async loadAvailableDates() {
    if (!this.MasterId || !this.SelectedServiceId) return;

    const requestId = ++this.availableDatesRequestId;

    this.IsLoadingDates = true;
    this.DatesError = "";
    this.AvailableDates = [];
    this.SelectedDate = "";
    this.SelectedSlot = null;
    this.Slots = [];
    this.Calendar = this.buildCalendar();

    const { from, to } = this.getAvailableDatesApiRange();

    try {
      const response = await CreateBookingService.GetAvailableDates(this.MasterId, this.SelectedServiceId, from, to);

      runInAction(() => {
        if (requestId !== this.availableDatesRequestId) return;

        this.AvailableDates = (response.dates ?? []).map((date) => this.normalizeAvailableDate(date)).filter(Boolean);

        this.Calendar = this.buildCalendar();
      });
    } catch {
      runInAction(() => {
        if (requestId !== this.availableDatesRequestId) return;

        this.DatesError = "Не удалось загрузить доступные дни";
        this.AvailableDates = [];
        this.Calendar = this.buildCalendar();
      });
    } finally {
      runInAction(() => {
        if (requestId !== this.availableDatesRequestId) return;

        this.IsLoadingDates = false;
      });
    }
  }

  private getAvailableDatesApiRange() {
    const from = new Date();
    from.setHours(0, 0, 0, 0);

    const to = new Date(from);
    to.setDate(to.getDate() + 89);

    return {
      from: this.formatDateForAvailableDatesApi(from),
      to: this.formatDateForAvailableDatesApi(to),
    };
  }

  private formatDateKey(date: Date) {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");

    return `${yyyy}-${mm}-${dd}`;
  }

  private formatDateForAvailableDatesApi(date: Date) {
    return this.formatDateKey(date);
  }

  private normalizeAvailableDate(value: string) {
    return value.trim();
  }

  public async selectDate(date: string) {
    if (!this.MasterId || !this.SelectedServiceId) return;

    if (this.SelectedDate === date) return;

    this.SelectedDate = date;
    this.SelectedSlot = null;
    this.Slots = [];

    const parsed = this.parseDateKey(date);
    this.CurrentMonthDate = new Date(parsed.getFullYear(), parsed.getMonth(), 1);
    this.Calendar = this.buildCalendar();

    this.SelectedDate = date;
    AnalyticsClient.track({
      eventType: "booking_date_selected",
      source: this.Source,
      entityType: "master",
      entityId: this.MasterId ?? undefined,
      metadata: {
        serviceId: this.SelectedServiceId,
        date,
      },
    });
    this.SelectedSlot = null;
    this.IsLoadingSlots = true;
    this.Error = "";

    try {
      const response = await CreateBookingService.GetAvailableSlots(this.MasterId, this.SelectedServiceId, date);

      runInAction(() => {
        this.Slots = this.filterAvailableSlotsByTime(response.slots ?? []);
      });
    } catch {
      runInAction(() => {
        this.Error = "Не удалось загрузить свободное время";
        this.Slots = [];
      });
    } finally {
      runInAction(() => {
        this.IsLoadingSlots = false;
      });
    }
  }

  public selectSlot(slot: IAvailableSlot) {
    if (!this.isSlotAvailableByTime(slot)) {
      this.Error = "Это время уже недоступно. Выберите другое время.";
      this.SelectedSlot = null;
      this.Slots = this.filterAvailableSlotsByTime(this.Slots);
      return;
    }

    this.Error = "";
    this.SelectedSlot = slot;
    AnalyticsClient.track({
      eventType: "booking_slot_selected",
      source: this.Source,
      entityType: "master",
      entityId: this.MasterId ?? undefined,
      metadata: {
        serviceId: this.SelectedServiceId,
        startsAt: slot.startsAt,
      },
    });
  }

  public get SelectedService() {
    return this.Services.find((x) => x.id === this.SelectedServiceId) ?? null;
  }

  private filterAvailableSlotsByTime(slots: IAvailableSlot[]) {
    return slots.filter((slot) => this.isSlotAvailableByTime(slot));
  }

  private isSlotAvailableByTime(slot: IAvailableSlot) {
    const minStartTime = Date.now() + MIN_BOOKING_DELAY_MINUTES * 60 * 1000;

    return new Date(slot.startsAt).getTime() >= minStartTime;
  }

  private buildCalendar(): IBookingCalendar {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const maxDate = new Date(today);
    maxDate.setDate(maxDate.getDate() + 89);

    const availableDatesSet = new Set(this.AvailableDates);

    const visibleMonth = new Date(this.CurrentMonthDate);
    const monthStart = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth(), 1);
    const monthEnd = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() + 1, 0);

    const start = new Date(monthStart);
    const startWeekday = start.getDay() === 0 ? 7 : start.getDay();
    start.setDate(start.getDate() - (startWeekday - 1));

    const end = new Date(monthEnd);
    const endWeekday = end.getDay() === 0 ? 7 : end.getDay();
    end.setDate(end.getDate() + (7 - endWeekday));

    const title = new Intl.DateTimeFormat("ru-RU", {
      month: "long",
      year: "numeric",
    }).format(visibleMonth);

    const days: ICalendarDay[] = [];
    const current = new Date(start);

    while (current <= end) {
      const dateKey = this.formatDateKey(current);

      const normalized = new Date(current);
      normalized.setHours(0, 0, 0, 0);

      const isInAllowedRange = normalized >= today && normalized <= maxDate;

      days.push({
        date: dateKey,
        dayNumber: current.getDate(),
        isCurrentMonth: current.getMonth() === visibleMonth.getMonth() && current.getFullYear() === visibleMonth.getFullYear(),
        isAvailableRange: isInAllowedRange && availableDatesSet.has(dateKey),
        isToday: normalized.getTime() === today.getTime(),
      });

      current.setDate(current.getDate() + 1);
    }

    return {
      title: title[0].toUpperCase() + title.slice(1),
      days,
    };
  }

  public setPhoneDraft(value: string) {
    if (this.HasSavedPhone) return;

    this.PhoneDraft = value;
    this.PhoneError = "";
  }

  public get PhoneDisplay() {
    return this.PhoneDraft.trim() || "";
  }

  public get IsPhoneReadonly() {
    return this.HasSavedPhone;
  }

  private validatePhone() {
    const phone = this.PhoneDraft.trim();

    if (!phone) {
      this.PhoneError = "Укажите номер телефона";
      return false;
    }

    // Мягкая фронтовая проверка. Нормализацию всё равно делает бэк.
    const digitsCount = phone.replace(/\D/g, "").length;

    if (digitsCount < 10) {
      this.PhoneError = "Проверьте номер телефона";
      return false;
    }

    this.PhoneError = "";
    return true;
  }
}
